import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Link from '@mui/material/Link';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeModeContext';

const LoginPage: React.FC = () => {
  const { user, login, isLoading, error } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/chat" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    try {
      const result = await login(email, password);
      // org_slug is now set by AuthContext from backend response
      
      // Check if password change is required
      if (result?.requires_password_change) {
        window.location.href = '/change-password';
      }
    } catch (error: any) {
      setLocalError(error.message || 'Login failed');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: mode === 'dark'
        ? 'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)'
        : 'linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: mode === 'dark'
          ? `radial-gradient(circle at 20% 80%, rgba(99,102,241,0.15) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(236,72,153,0.15) 0%, transparent 50%),
             radial-gradient(circle at 40% 40%, rgba(139,92,246,0.08) 0%, transparent 50%)`
          : `radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)`,
        pointerEvents: 'none',
      }
    }}>
      {/* Theme Toggle Button */}
      <IconButton
        aria-label="Toggle color theme"
        onClick={toggleMode}
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          zIndex: 10,
          color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          backgroundColor: mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: mode === 'dark' ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.3)' : '0 8px 24px rgba(15,23,42,0.08)',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.15s ease-in-out',
        }}
        title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card 
          className="login-card-animation"
          sx={{
            borderRadius: '32px',
            background: mode === 'dark' ? 'rgba(2, 6, 23, 0.75)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(24px)',
            border: mode === 'dark' ? '1px solid rgba(51, 65, 85, 0.6)' : '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: `
              0 8px 32px rgba(15, 23, 42, ${mode === 'dark' ? '0.45' : '0.08'}),
              0 0 0 1px ${mode === 'dark' ? 'rgba(51, 65, 85, 0.6)' : 'rgba(255, 255, 255, 0.9)'},
              inset 0 1px 0 ${mode === 'dark' ? 'rgba(51, 65, 85, 0.4)' : 'rgba(255, 255, 255, 0.9)'}
            `,
            overflow: 'hidden',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `
                0 12px 48px rgba(15, 23, 42, ${mode === 'dark' ? '0.6' : '0.12'}),
                0 0 0 1px ${mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.2)'},
                inset 0 1px 0 ${mode === 'dark' ? 'rgba(51, 65, 85, 0.5)' : 'rgba(255, 255, 255, 0.9)'}
              `,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: mode === 'dark'
                ? 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.35) 50%, transparent 100%)'
                : 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.3) 50%, transparent 100%)',
            }
          }}>
          <CardContent sx={{ p: 6 }}>
            <Box textAlign="center" mb={5} className="animate-fade-in-up">
              <Box mb={3} sx={{ position: 'relative' }}>
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                  borderRadius: '50%',
                  opacity: 0.1,
                  animation: 'pulse 2s ease-in-out infinite',
                }} />
                <img 
                  src="/atlasai-icon.svg" 
                  alt="CoreSupport" 
                  style={{ 
                    width: 72, 
                    height: 72, 
                    marginBottom: 16,
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(99, 102, 241, 0.2))'
                  }}
                  className="floating"
                />
              </Box>
              <Typography variant="h3" component="h1" gutterBottom sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800,
                letterSpacing: '-0.025em',
                mb: 1
              }}>
                CoreSupport
              </Typography>
              <Typography variant="h6" sx={{ 
                color: 'text.secondary', 
                fontWeight: 400, 
                mb: 1,
                letterSpacing: '0.025em'
              }}>
                Intelligent Support Platform
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary',
                maxWidth: 400,
                mx: 'auto',
                lineHeight: 1.6
              }}>
                Welcome back! Sign in to access your AI-powered support experience
              </Typography>
            </Box>

            {(error || localError) && (
              <Alert 
                severity="error" 
                className="animate-fade-in-up"
                sx={{ 
                  mb: 3,
                  borderRadius: '16px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '& .MuiAlert-icon': {
                    color: '#dc2626'
                  }
                }}
              >
                {error || localError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ 
                  color: '#475569', 
                  mb: 1, 
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="user@company.com"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '56px',
                      fontSize: '1rem',
                      backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.9)'} !important`,
                      color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                      '& fieldset': {
                        borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.5)' : 'rgba(226, 232, 240, 0.8)',
                      },
                      '&:hover': {
                        backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 1)'} !important`,
                        '& fieldset': {
                          borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.4)',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 1)'} !important`,
                        '& fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        color: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: mode === 'dark' 
                            ? '0 0 0 100px rgba(15, 23, 42, 0.9) inset !important'
                            : '0 0 0 100px rgba(255, 255, 255, 1) inset !important',
                          WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        },
                        '&::placeholder': {
                          color: '#94a3b8 !important',
                          opacity: '1 !important',
                          WebkitTextFillColor: '#94a3b8 !important',
                        },
                      },
                      '& input': {
                        color: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: mode === 'dark' 
                            ? '0 0 0 100px rgba(15, 23, 42, 0.9) inset !important'
                            : '0 0 0 100px rgba(255, 255, 255, 1) inset !important',
                          WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        },
                        '&::placeholder': {
                          color: '#94a3b8 !important',
                          opacity: '1 !important',
                          WebkitTextFillColor: '#94a3b8 !important',
                        },
                      },
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      }
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ 
                  color: '#475569', 
                  mb: 1, 
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="Enter your password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          sx={{
                            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '56px',
                      fontSize: '1rem',
                      backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.9)'} !important`,
                      color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                      '& fieldset': {
                        borderColor: mode === 'dark' ? 'rgba(71, 85, 105, 0.5)' : 'rgba(226, 232, 240, 0.8)',
                      },
                      '&:hover': {
                        backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 1)'} !important`,
                        '& fieldset': {
                          borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.4)',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${mode === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 1)'} !important`,
                        '& fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        color: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: mode === 'dark' 
                            ? '0 0 0 100px rgba(15, 23, 42, 0.9) inset !important'
                            : '0 0 0 100px rgba(255, 255, 255, 1) inset !important',
                          WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        },
                        '&::placeholder': {
                          color: '#94a3b8 !important',
                          opacity: '1 !important',
                          WebkitTextFillColor: '#94a3b8 !important',
                        },
                      },
                      '& input': {
                        color: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: mode === 'dark' 
                            ? '0 0 0 100px rgba(15, 23, 42, 0.9) inset !important'
                            : '0 0 0 100px rgba(255, 255, 255, 1) inset !important',
                          WebkitTextFillColor: `${mode === 'dark' ? '#f8fafc' : '#0f172a'} !important`,
                        },
                        '&::placeholder': {
                          color: '#94a3b8 !important',
                          opacity: '1 !important',
                          WebkitTextFillColor: '#94a3b8 !important',
                        },
                      },
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      }
                    }
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ 
                  height: '56px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: '16px',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.35)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
                    transform: 'none',
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Sign In to CoreSupport'
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </Box>

            {/* Demo accounts removed */}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LoginPage;
